import React, { useState } from 'react';
import { useAppContext } from '../../Services/AppContext';
import MedicalReportDetailLatam from './MedicalReportDetailLatam';
import MedicalReportDetailDefault from './MedicalReportDetailDefault';
import apiClient from '../../Services/ApiClient';
import useNotification from '../../Hooks/useNotification.hook';
import useTranslation from '../../Hooks/useTranslation';
import withOverlayLoading from '../../Libs/withOverlayLoading';
import { useEffectAsync } from '../../helpers';
import {
	useLazyIsMeddiCaseQuery,
	useLazyRootWorkplaceByChildQuery,
} from '../../graphql/graphql';
import { Partner } from '../../Enums/Partner.enum';
import MedicalReportDetailMou from './MedicalReportDetailMou';
import { useDiscourseContext } from '../../Modules/Discourse/Services/DiscourseContext';
import MedicalRequestCreateModal from '../../Modules/MedicalRequests/Components/MedicalRequestCreate/MedicalRequestCreateModal';
import MedicalReportDetailSerbia from './Serbia/MedicalReportDetailSerbia';
import MedicalReportDetailPoland from './Poland/MedicalReportDetailPoland';
import MedicalReportDetailHungary from './Hungary/MedicalReportDetailHungary';
import MedicalReportDetailGermany from './Germany/MedicalReportDetailGermany';
import useJob from '../../Modules/Base/Hooks/useJob';
import useLoading from '../../Modules/Base/Hooks/useLoading';
import MedicalReportSlovakia from './Slovakia/MedicalReportSlovakia';

interface MedicalReportDetailWrapperProps {
	patientId: string;
	workplaceId: string;
	loggedUserId: string;
	caseId?: string;
	onSubmit?: () => void;
	defaultData?: any;
	customSuccess?: (values: any) => void;
	onMedicalReportCreate?: () => void;
	setLoading: (newState: boolean) => void;
	attachToDiscourse?: boolean;
	disableSubmit?: boolean;
	createDekurz?: boolean;
}

const MedicalReportDetailWrapper: React.FC<MedicalReportDetailWrapperProps> = ({
	patientId,
	workplaceId,
	loggedUserId,
	defaultData,
	caseId,
	onSubmit,
	customSuccess,
	setLoading,
	onMedicalReportCreate,
	attachToDiscourse = false,
	disableSubmit,
	createDekurz = true,
}) => {
	const { globalTemplate } = useAppContext();
	const { showNotificationSuccess, showNotificationAlert } = useNotification();
	const { t } = useTranslation();
	const [getRootWorkplace] = useLazyRootWorkplaceByChildQuery();
	const [type, setType] = useState('default');
	const { handleAttachToDiscourse } = useDiscourseContext();
	const { handleStartWaitingForJob, RenderJobWaiting } = useJob();
	const { isLoading, setIsLoading, RenderLoading } = useLoading(true);
	const [getIsMeddiCase] = useLazyIsMeddiCaseQuery();
	const [isMeddiCase, setIsMeddiCase] = useState(false);

	const [isMedicalRequestCreateOpen, setIsMedicalRequestCreateOpen] =
		useState(false);
	const [createdDocumentData, setCreatedDocumentData] = useState<
		{ doctorId?: string; patientId?: string } | undefined
	>(undefined);

	useEffectAsync(async () => {
		const { workplace } = await getRootWorkplace({
			workplaceId,
		}).unwrap();
		if (workplace?.rootWorkplace?.partnerId === Partner.MOU_MEDDI) {
			setType('mou');
		}

		if (caseId) {
			const { data } = await getIsMeddiCase({
				id: caseId,
			});
			setIsMeddiCase(data?.case?.isMeddi ?? false);
		}
		setIsLoading(false);
	});

	const runFinalSteps = async (doctorId?: string, patientId?: string) => {
		if (attachToDiscourse && handleAttachToDiscourse) {
			// attach doctor (only latam)
			if (doctorId) {
				await handleAttachToDiscourse(doctorId, 'medicalReport');
			}

			// attach patient
			if (patientId) {
				await handleAttachToDiscourse(patientId, 'medicalReportPatient');
			}
		}

		onSubmit?.();
		onMedicalReportCreate?.();
	};

	const handleSaveReport = async (values: any) => {
		if (customSuccess) {
			customSuccess(values);
		} else {
			await handleSaveMedicalReport(values);
		}
	};

	const checkJobs = (documents: { id: string; jobId?: string }[]) => {
		const jobIds: string[] = [];
		documents.forEach(document => {
			if (document.jobId) {
				jobIds.push(document.jobId);
			}
		});
		return jobIds;
	};

	const handleSaveMedicalReport = async (values: any) => {
		setLoading(true);
		try {
			const { data: document } = await apiClient.post(
				'/user/' + patientId + '/medical-report',
				{
					data: {
						rawData: values,
						caseId: caseId,
					},
					createDekurz,
				},
			);

			if (document.error) {
				setLoading(false);
				return;
			}

			if (document) {
				const doctorId = document.doctor?.id;
				const patientId = document.patient?.id;

				setCreatedDocumentData({
					doctorId,
					patientId,
				});

				showNotificationSuccess({
					content: t('base.medicalReportSaved'),
				});

				if (values.contactSpecialist) {
					setIsMedicalRequestCreateOpen(true);
				} else {
					//CHECK IF THERE ARE ANY JOBS TO WAIT FOR
					const jobIds = checkJobs(document.patientsDocuments);
					if (jobIds.length > 0) {
						//STAR WAITING FOR EACH JOB
						jobIds.forEach(jobId => {
							handleStartWaitingForJob(jobId);
						});
					} else {
						await runFinalSteps(doctorId, patientId);
					}
				}
			}
		} catch (e: any) {
			if (e.response?.data?.displayableError) {
				showNotificationAlert({
					content: e.response.data.message,
				});
			}
		}
		setLoading(false);
	};

	const handleAfterCreateMedicalRequest = async () => {
		setIsMedicalRequestCreateOpen(false);
		await runFinalSteps(
			createdDocumentData?.doctorId,
			createdDocumentData?.patientId,
		);
	};

	let component;

	if (isLoading) {
		return <RenderLoading />;
	}

	switch (globalTemplate) {
		case 'peru':
		case 'ecuador':
		case 'mexico':
		case 'colombia':
			component = (
				<MedicalReportDetailLatam
					patientId={patientId}
					workplaceId={workplaceId}
					loggedUserId={loggedUserId}
					defaultData={defaultData}
					onSuccess={handleSaveMedicalReport}
					customSuccess={customSuccess}
					disableSubmit={disableSubmit}
				/>
			);
			break;
		case 'serbia':
			component = (
				<MedicalReportDetailSerbia
					patientId={patientId}
					workplaceId={workplaceId}
					loggedUserId={loggedUserId}
					defaultData={defaultData}
					onSuccess={handleSaveReport}
					disableSubmit={disableSubmit}
				/>
			);
			break;
		case 'germany':
			component = (
				<MedicalReportDetailGermany
					patientId={patientId}
					workplaceId={workplaceId}
					loggedUserId={loggedUserId}
					defaultData={defaultData}
					onSuccess={handleSaveReport}
					disableSubmit={disableSubmit}
					isMeddiCase={isMeddiCase}
				/>
			);
			break;
		case 'poland':
			component = (
				<MedicalReportDetailPoland
					patientId={patientId}
					workplaceId={workplaceId}
					loggedUserId={loggedUserId}
					defaultData={defaultData}
					onSuccess={handleSaveReport}
					disableSubmit={disableSubmit}
					isMeddiCase={isMeddiCase}
				/>
			);
			break;
		case 'slovakia':
			component = (
				<MedicalReportSlovakia
					patientId={patientId}
					caseId={caseId}
					onSuccess={handleAfterCreateMedicalRequest}
				/>
			);
			break;
		case 'hungary':
			component = (
				<MedicalReportDetailHungary
					patientId={patientId}
					workplaceId={workplaceId}
					loggedUserId={loggedUserId}
					defaultData={defaultData}
					onSuccess={handleSaveReport}
					disableSubmit={disableSubmit}
					isMeddiCase={isMeddiCase}
				/>
			);
			break;
		default:
			if (type === 'mou') {
				component = (
					<MedicalReportDetailMou
						patientId={patientId}
						workplaceId={workplaceId}
						loggedUserId={loggedUserId}
						defaultData={defaultData}
						onSuccess={handleSaveMedicalReport}
						customSuccess={customSuccess}
					/>
				);
			} else {
				component = (
					<>
						<MedicalReportDetailDefault
							patientId={patientId}
							workplaceId={workplaceId}
							loggedUserId={loggedUserId}
							defaultData={defaultData}
							onSuccess={handleSaveMedicalReport}
							customSuccess={customSuccess}
							isMeddiCase={isMeddiCase}
						/>
						{isMedicalRequestCreateOpen && (
							<MedicalRequestCreateModal
								isOpen
								setIsOpen={handleAfterCreateMedicalRequest}
								patientId={patientId}
								onCreate={handleAfterCreateMedicalRequest}
								autoOpenByKey='medicalRequestFormK'
								caseId={caseId}
							/>
						)}
					</>
				);
			}
			break;
	}

	return (
		<>
			{component}
			<RenderJobWaiting
				onJobSuccess={handleAfterCreateMedicalRequest}
				onJobFailed={handleAfterCreateMedicalRequest}
				onJobSkipWaiting={handleAfterCreateMedicalRequest}
				waitingMessage='component.mediaViewer.documentIsSigning'
				failedMessage='component.mediaViewer.documentSignFailed'
				timeout={180000}
			/>
		</>
	);
};

export default withOverlayLoading(MedicalReportDetailWrapper);
